import { Search } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Card, Divider, Grid, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  getCandidateSubmissionSettingsTemplatesList,
  deleteCandidateSubmissionSettings,
  ICandidateSubmissionSettingsSearch,
} from 'wisestep-api';
import {
  SearchBar,
  WsButton,
  WsColors,
  WsIconNoUserGeneric,
  WsListingLoader,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
  timeSince,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard/NoDataFoundCard';
import { SettingPageBreadCrumbs } from '../../../components/SettingPageBreadCrumbs/SettingPageBreadCrumbs';
import { useWorkspaceContext } from '../../../context/WorkspaceContext/WorkspaceContext';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import ErrorLogger from '../../../providers/ErrorLogger';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
type SortModel = {
  field: string;
  sort: 'asc' | 'desc' | undefined;
}[];

export interface ISubmissionTemplateList {
  content: any[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const SubmissionTemplates = () => {
  const methods = useForm();
  const navigate = useNavigate();
  const { id: workspaceId } = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [templatesList, setTemplatesList] = useState<ISubmissionTemplateList | null>(null);
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel, setSortModel] = useState<SortModel>([
    {
      field: 'createdOn',
      sort: 'desc',
    },
  ]);

  const fetchTemplates = useCallback(
    async (query = null) => {
      setLoader(true);
      try {
        const res = await getCandidateSubmissionSettingsTemplatesList({
          workspaceId: workspaceId || '',
          templateName: query || '',
          page: Math.max(page - 1, 0),
          size: pageSize ?? 10,
        } as ICandidateSubmissionSettingsSearch);
        setTemplatesList(res?.data);
      } catch (error) {
        setTemplatesList(null);
      } finally {
        setLoader(false);
      }
    },
    [page, pageSize, workspaceId]
  );

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const tableData = useMemo(
    () =>
      templatesList?.content?.map((template, index) => ({
        id: index,
        templateName: template.templateName || '',
        createdOn: new Date(template.createdOn) || '',
        tag: template?.tags || '',
      })) || [],
    [templatesList]
  );
  const onAddClick = () => navigate('/crm-settings/configure-submission-email-template');
  const handleEdit = (row) =>
    navigate(`/crm-settings/configure-submission-email-template?templateName=${row.templateName}`);

  const handleDelete = async (row) => {
    try {
      const res = await deleteCandidateSubmissionSettings(row.templateName, workspaceId ?? '');
      if (res.data) {
        setNotification(success('Template Deleted Successfully'));
        fetchTemplates();
      } else {
        setNotification(failed('Error while deleting template'));
      }
    } catch (error) {
      setNotification(failed('Exception while deleting template'));
    }
  };

  const onSearch = () => fetchTemplates(methods.watch('query'));
  const onclear = () => {
    methods.setValue('query', '');
    fetchTemplates();
  };

  const columns = [
    {
      field: 'templateName',
      headerName: 'Template Name',
      sortable: true,
      flex: 2,
      disableColumnMenu: true,
      renderCell: (params) => (
        <WsTypography bold={true} variant={WsTypoVariants.SUB_TITLE}>
          {params.value}
        </WsTypography>
      ),
    },
    {
      field: 'tag',
      headerName: 'Tag',
      sortable: true,
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: '#ECFEFF',
            color: '#0E7490',
            padding: '2px 10px',
            gap: '8px',
            borderRadius: '4px',
            border: '1px solid #67E8F9',
            display: 'inline-block',
            fontSize: '0.875rem',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      sortable: true,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => {
        return <span>{timeSince(params.value ?? '')}</span>;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      disableColumnMenu: true,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton aria-label="edit" onClick={() => handleEdit(params.row)}>
            <EditIcon color="primary" />
          </IconButton>
          <IconButton aria-label="delete" onClick={() => handleDelete(params.row)}>
            <DeleteIcon sx={{ color: 'red' }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <SettingPageBreadCrumbs
        breadCrumbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.CRM,
          Pages['SUBMISSION_FORM_TEMPLATES']
        )}
      >
        <Grid
          item
          container
          direction="column"
          bgcolor={WsColors.White}
          p={2}
          spacing={1}
          sx={{
            borderRadius: '12px',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={7}>
              <Box display="flex" flexDirection="column" gap={1}>
                <WsTypography bold={true} variant={WsTypoVariants.TITLE}>
                  Customize Submission Flow
                </WsTypography>
                <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.TERTIARY900}>
                  {templatesList?.totalElements || 0} templates
                </WsTypography>
              </Box>
            </Grid>
            <Grid item xs={5} container justifyContent="flex-end">
              <WsButton startIcon={<AddIcon />} variant="contained" onClick={onAddClick}>
                Add Template
              </WsButton>
            </Grid>
          </Grid>
          <Divider sx={{ backgroundColor: 'grey', my: 2 }} />
          <Grid item container justifyContent="flex-start" spacing={1} xs={5}>
            <FormProvider {...methods}>
              <Grid item xs={5}>
                <Card sx={{ p: 0.5, height: 32 }}>
                  <SearchBar name="query" placeholder={'Search Template or Tag'} />
                </Card>
              </Grid>
              <Grid item xs={4} gap={1} display="flex">
                <WsButton
                  className="advance-filter"
                  variant="outlined"
                  size="medium"
                  type="reset"
                  onClick={onclear}
                >
                  Clear
                </WsButton>
                <WsButton
                  className="advance-filter"
                  variant="contained"
                  size="medium"
                  type="submit"
                  onClick={onSearch}
                  startIcon={<Search />}
                >
                  Search
                </WsButton>
              </Grid>
            </FormProvider>
          </Grid>
          <Grid item>
            {isEmpty(templatesList) && !loader ? (
              <NoDataFoundCard
                text="No Template are created"
                icon={<WsIconNoUserGeneric />}
                subElement={<WsTypography>No Templates</WsTypography>}
              />
            ) : loader ? (
              <WsListingLoader />
            ) : (
              <>
                <DataGrid
                  autoHeight
                  columns={columns}
                  rows={tableData}
                  page={page - 1}
                  rowCount={templatesList?.totalElements}
                  pageSize={pageSize}
                  paginationMode="server"
                  rowsPerPageOptions={[5, 10, 15, 20]}
                  onPageChange={(newPage) => setPage(newPage + 1)}
                  onPageSizeChange={(newSize) => setPageSize(newSize)}
                  sortingMode="client"
                  sortModel={sortModel}
                  onSortModelChange={(model) => setSortModel(model as SortModel)}
                  disableSelectionOnClick
                  getRowHeight={() => 60}
                  sx={{
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f9f9f9',
                    },
                    '& .MuiDataGrid-row:nth-child(even)': {
                      backgroundColor: '#f9f9f9',
                    },
                    '& .MuiDataGrid-footer': {
                      justifyContent: 'center',
                    },
                    '& .MuiDataGrid-pagination': {
                      display: 'flex',
                      justifyContent: 'flex-end',
                    },
                  }}
                />
              </>
            )}
          </Grid>
        </Grid>
      </SettingPageBreadCrumbs>
    </ErrorBoundary>
  );
};

export default SubmissionTemplates;
